import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import { useEffect, useMemo, useRef } from 'react';
import { IS_COMMENT_MODE_ACTIVE } from '../constants/CollaborationSidebarStateProperties';
import { DEEP_COMMENTING_REDESIGN_TOUR_ID } from 'collaboration-sidebar/features/deepCommenting/constants/deepCommentingOnboarding';
import { SUPPORTED_HUBS_REQUIRED_SCOPES } from '../constants/supportedHubs';
import CollaborationSidebarStateContext from '../contexts/CollaborationSidebarStateContext';
import UserSettingsProvider from '../contexts/UserSettingsProvider'; // @ts-expect-error no-type

import usePersistPropsToCollaborationSidebarState from '../hooks/usePersistPropsToCollaborationSidebarState';
// @ts-expect-error no-type
import { showWarningNotification } from '../utils/notifications'; // @ts-expect-error no-type

import { getIsCommentingUrl, getIsWorkflowActionsUrl } from '../utils/urls'; // @ts-expect-error no-type

import AsyncOnboardingTour from 'collaboration-sidebar/features/deepCommenting/components/AsyncOnboardingTour'; // @ts-expect-error no-type

import CommentModeIndicator from 'collaboration-sidebar/features/deepCommenting/components/CommentModeIndicator';
import LayoutWithRightSidebar from './LayoutWithRightSidebar'; // @ts-expect-error no-type

import AsyncCollaborationSidebarContainerWithSidebarSkeleton from 'collaboration-sidebar/features/fixedSidebar/components/AsyncCollaborationSidebarContainerWithSidebarSkeleton'; // @ts-expect-error no-type

import AsyncCollaborationSidebarContainerWithoutSidebarSkeleton from './sidebar/CollaborationSidebar/AsyncCollaborationSidebarContainerWithoutSidebarSkeleton';
import { FLOATING_SIDEBAR_TOUR_COMPLETE } from 'collaboration-sidebar/constants/localStorageKeys';
import { FLOATING_SIDEBAR_TOUR_ID, RESOLVABLE_COMMENTS_TOUR_ID } from 'collaboration-sidebar/constants/tourIDs';
import useOnboardingTour from '../hooks/useOnboardingTour';
import { hasScope } from '../utils/auth';
import useResolvableCommentsTour from '../hooks/useResolvableCommentsTour';
import { FEATURES } from '../constants';
/**
 * This component assumes collaborationSidebarState and
 * onUpdateCollaborationSidebarState are passed in. See
 * LayoutWithCollaborationSidebar for example
 * implementation
 */

function ControlledLayoutWithCollaborationSidebar(_ref) {
  let {
    appHub,
    children
  } = _ref,
      collaborationSidebarContainerProps = _objectWithoutPropertiesLoose(_ref, ["appHub", "children"]);

  const hasShownNoPermissionWarning = useRef(false);
  const pageContentRef = useRef(null);
  const {
    objectType,
    objectId,
    isListingPage,
    collaborationSidebarState,
    onUpdateCollaborationSidebarState,
    isControlled,
    floating,
    enabledFeatures
  } = collaborationSidebarContainerProps;
  const collaborationSidebarStateWithPersistedProps = usePersistPropsToCollaborationSidebarState({
    collaborationSidebarState,
    onUpdateCollaborationSidebarState,
    collaborationSidebarProps: collaborationSidebarContainerProps
  });
  const hasSidebarPermission = useMemo(() => {
    if (!appHub) {
      return false;
    }

    if (Array.isArray(appHub)) {
      return Boolean(appHub.find(hub => hasScope(SUPPORTED_HUBS_REQUIRED_SCOPES[hub])));
    }

    return hasScope(SUPPORTED_HUBS_REQUIRED_SCOPES[appHub]);
  }, [appHub]);
  useEffect(() => {
    if (!hasShownNoPermissionWarning.current && !hasSidebarPermission && getIsCommentingUrl()) {
      showWarningNotification({
        title: I18n.text('collaborationSidebar.notifications.failure.missingPermissionCommentingTitle'),
        message: I18n.text('collaborationSidebar.notifications.failure.missingPermissionCommentingBody'),
        sticky: true
      });
      hasShownNoPermissionWarning.current = true;
    }
  }, [hasSidebarPermission]);
  const {
    isCommentModeActive,
    isEmbeddedCommentingOnboardingEnabled,
    isOpen
  } = collaborationSidebarContainerProps.collaborationSidebarState;
  const showFloatingSidebarTour = useOnboardingTour({
    featureIsVisible: floating && !getIsWorkflowActionsUrl(),
    localStorageKey: FLOATING_SIDEBAR_TOUR_COMPLETE,
    tourId: FLOATING_SIDEBAR_TOUR_ID,
    objectType
  });
  const showResolvableCommentsTour = useResolvableCommentsTour({
    objectType,
    objectId,
    isOpen
  });

  if (!hasSidebarPermission) {
    if (children) {
      return children;
    }

    return null;
  }
  /**
   * Campaigns in the sidebar is not being actively worked on.
   * We're keeping it in the codebase for now as it may be
   * prioritized with ongoing campaign templates improvements.
   */


  const enabledFeaturesWithoutCampaigns = enabledFeatures ? enabledFeatures.filter(feature => feature !== FEATURES.CAMPAIGNS) : enabledFeatures;
  return /*#__PURE__*/_jsx(CollaborationSidebarStateContext.Provider, {
    value: {
      collaborationSidebarState: collaborationSidebarStateWithPersistedProps,
      onUpdateCollaborationSidebarState
    },
    children: /*#__PURE__*/_jsxs(UserSettingsProvider, {
      objectType: objectType,
      objectId: objectId,
      children: [isEmbeddedCommentingOnboardingEnabled && /*#__PURE__*/_jsx(AsyncOnboardingTour, {
        tourId: DEEP_COMMENTING_REDESIGN_TOUR_ID
      }), showFloatingSidebarTour && /*#__PURE__*/_jsx(AsyncOnboardingTour, {
        tourId: FLOATING_SIDEBAR_TOUR_ID
      }), showResolvableCommentsTour && /*#__PURE__*/_jsx(AsyncOnboardingTour, {
        tourId: RESOLVABLE_COMMENTS_TOUR_ID
      }), /*#__PURE__*/_jsxs(LayoutWithRightSidebar, {
        sidebar: (objectId || isListingPage) && objectType && (floating ? /*#__PURE__*/_jsx(AsyncCollaborationSidebarContainerWithoutSidebarSkeleton, Object.assign({
          pageContentRef: pageContentRef
        }, collaborationSidebarContainerProps, {
          collaborationSidebarState: collaborationSidebarStateWithPersistedProps,
          enabledFeatures: enabledFeaturesWithoutCampaigns
        })) : /*#__PURE__*/_jsx(AsyncCollaborationSidebarContainerWithSidebarSkeleton, Object.assign({
          pageContentRef: pageContentRef
        }, collaborationSidebarContainerProps, {
          collaborationSidebarState: collaborationSidebarStateWithPersistedProps,
          enabledFeatures: enabledFeaturesWithoutCampaigns
        }))),
        isControlled: isControlled,
        ref: pageContentRef,
        children: [children, isCommentModeActive && /*#__PURE__*/_jsx(CommentModeIndicator, {
          onCancel: () => {
            onUpdateCollaborationSidebarState({
              [IS_COMMENT_MODE_ACTIVE]: false
            });
          }
        })]
      })]
    })
  });
}

ControlledLayoutWithCollaborationSidebar.defaultProps = {
  isControlled: true
};
export default ControlledLayoutWithCollaborationSidebar;